import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { nanoid } from 'nanoid'

import { FileInfo, uploadFileWithProgress as _uploadFileWithProgress, useGetFileByIdQuery, useLazyGetFileByIdQuery } from '../../store/media/file'
import { useCreateDocumentMutation, useDeleteDocumentMutation, useMarkDocumentFileUploadedMutation } from '../../store/ai/documents'
import { useGetRetentionPoliciesQuery } from '../../store/account/account'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppSelector } from '../../store/hooks'
import { useListAiAgentResultQuery } from '../../store/ai/aiAgentResult'
import { useGetMeQuery } from '../../store/user/user'
import { AIAgentType } from '../../models/AIAgent'

import { RetentionResourceType, RetentionRuleType } from '../../models/RetentionPolicy'

import { type IntegrationServiceId } from '../../common/integrations'
import { getConnectedDocumentServiceIds } from '../../common/getConnectedDocumentServiceIds'
import { type FileBrowserItem } from '../../components/DocumentFileBrowser/types'

import { DocumentUploadSource, hashFile, inferContentType, MAX_DOCUMENT_BATCH_FILES } from './DocumentUpload/utils'
import { DocumentCreate, DocumentFile, DocumentFileType } from '../../models/Document'
import {
  DocumentConnectorItem,
  useGetDocumentConnectorFileQuery,
  useLazyGetDocumentConnectorFileQuery,
} from '../../store/documentConnectors/documentConnectors'
import { DocumentFileInfo, selectDocument } from '../../store/processDocument/document'
import { skipToken } from '@reduxjs/toolkit/query'

export interface ZocksDocumentUploadQueueItem {
  source: DocumentUploadSource.ZOCKS
  id: string
  file: File
  isUploading: boolean
  isUploaded: boolean
  abortController: AbortController
  documentId?: string
  progress?: number
  hash: string
}

export interface IntegrationDocumentUploadQueueItem {
  source: DocumentUploadSource.INTEGRATION
  id: string
  serviceId: IntegrationServiceId
  fileId: string
  name: string
  size: number
  documentId?: string
}

export type DocumentUploadQueueItem = ZocksDocumentUploadQueueItem | IntegrationDocumentUploadQueueItem

const isQueueItemReady = (item: DocumentUploadQueueItem) => (item.source === DocumentUploadSource.INTEGRATION ? !!item.documentId : item.isUploaded)

export const useDocumentFileUpload = () => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerCreateDocument] = useCreateDocumentMutation()
  const [markDocumentFileUploaded] = useMarkDocumentFileUploadedMutation()
  const [triggerDeleteDocument] = useDeleteDocumentMutation()

  const [uploadQueueItems, setUploadQueueItems] = useState<DocumentUploadQueueItem[]>([])

  const clearedItemIdsRef = useRef<Set<string>>(new Set())

  const onProgress = (itemId: string, progress: number) => {
    setUploadQueueItems((prev) => prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, progress } : item)))
  }

  const onSuccess = (itemId: string) => {
    setUploadQueueItems((prev) =>
      prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, isUploading: false, isUploaded: true } : item)),
    )
  }

  const onError = (itemId: string) => {
    setUploadQueueItems((prev) =>
      prev.map((item) => (item.source === DocumentUploadSource.ZOCKS && item.id === itemId ? { ...item, isUploading: false } : item)),
    )
  }

  const createDraftDocument = async (itemId: string, payload: DocumentCreate) => {
    const { document, fileUploadTargets } = await triggerCreateDocument({ accountId, payload }).unwrap()

    if (clearedItemIdsRef.current.has(itemId)) {
      clearedItemIdsRef.current.delete(itemId)
      await triggerDeleteDocument({ accountId, documentId: document.id })
      return null
    }

    setUploadQueueItems((prev) => prev.map((item) => (item.id === itemId ? { ...item, documentId: document.id } : item)))

    return { documentId: document.id, uploadTarget: fileUploadTargets[0] }
  }

  const createDocumentAndUploadFile = async (itemId: string, file: File, hash: string, signal: AbortSignal) => {
    const payload: DocumentCreate = {
      name: file.name,
      files: [{ type: DocumentFileType.ZOCKS, name: file.name, size: file.size, contentType: file.type, hash }],
    }

    const draft = await createDraftDocument(itemId, payload)
    if (!draft) {
      return
    }

    const { uploadTarget } = draft
    if (!uploadTarget) {
      onError(itemId)
      return
    }

    await _uploadFileWithProgress(uploadTarget.fileUploadUrl, uploadTarget.fileUploadParameters, file, (progress) => onProgress(itemId, progress), signal)

    await markDocumentFileUploaded({ accountId, documentId: uploadTarget.documentId, fileId: uploadTarget.fileId }).unwrap()

    onSuccess(itemId)
  }

  const onClear = async (itemId: string) => {
    const item = uploadQueueItems.find((item) => item.id === itemId)
    if (!item) {
      return
    }

    setUploadQueueItems((prev) => prev.filter((item) => item.id !== itemId))

    try {
      if (!item.documentId) {
        clearedItemIdsRef.current.add(itemId)
        if (item.source === DocumentUploadSource.ZOCKS) {
          item.abortController.abort()
        }
        return
      }

      if (item.source === DocumentUploadSource.ZOCKS && item.isUploading) {
        item.abortController.abort()
      }

      await triggerDeleteDocument({ accountId, documentId: item.documentId })
    } catch (e) {
      console.error(e)
    }
  }

  const onSingleFileSelected = async (file: File) => {
    const itemId = nanoid()
    const abortController = new AbortController()

    try {
      const hash = await hashFile(file)

      setUploadQueueItems((prev) => [
        ...prev,
        { source: DocumentUploadSource.ZOCKS, id: itemId, isUploading: true, isUploaded: false, file, abortController, hash },
      ])

      await createDocumentAndUploadFile(itemId, file, hash, abortController.signal)
    } catch (e) {
      console.error(e)
      onError(itemId)
    }
  }

  const onFilesSelected = async (files: File[]) => {
    await Promise.all(files.map(onSingleFileSelected))
  }

  const createIntegrationDocument = async (item: IntegrationDocumentUploadQueueItem) => {
    const payload: DocumentCreate = {
      name: item.name,
      files: [
        {
          type: DocumentFileType.INTEGRATION,
          fileId: item.fileId,
          connector: item.serviceId,
          name: item.name,
          size: item.size,
          contentType: inferContentType(item.name),
        },
      ],
    }

    try {
      await createDraftDocument(item.id, payload)
    } catch (e) {
      console.error(e)
    }
  }

  const onIntegrationFilesSelected = async (serviceId: IntegrationServiceId, items: FileBrowserItem[]) => {
    const remaining = MAX_DOCUMENT_BATCH_FILES - uploadQueueItems.length

    const newItems: IntegrationDocumentUploadQueueItem[] = items
      .filter((item) => item.type === 'file')
      .slice(0, Math.max(0, remaining))
      .map((item) => ({ source: DocumentUploadSource.INTEGRATION, id: nanoid(), serviceId, fileId: item.id, name: item.name, size: item.size ?? 0 }))

    setUploadQueueItems((prev) => [...prev, ...newItems])

    await Promise.all(newItems.map(createIntegrationDocument))
  }

  const onRetry = async (itemId: string) => {
    const item = uploadQueueItems.find((item) => item.id === itemId)
    if (!item || item.source !== DocumentUploadSource.ZOCKS) {
      return
    }

    const abortController = new AbortController()
    setUploadQueueItems((prev) =>
      prev.map((item) =>
        item.source === DocumentUploadSource.ZOCKS && item.id === itemId
          ? { ...item, isUploading: true, isUploaded: false, progress: 0, documentId: undefined, abortController }
          : item,
      ),
    )

    if (item.documentId) {
      try {
        await triggerDeleteDocument({ accountId, documentId: item.documentId })
      } catch (e) {
        console.error(e)
      }
    }

    try {
      await createDocumentAndUploadFile(itemId, item.file, item.hash, abortController.signal)
    } catch (e) {
      console.error(e)
      onError(itemId)
    }
  }

  const readyCount = uploadQueueItems.filter(isQueueItemReady).length
  const allItemsReady = uploadQueueItems.length > 0 && readyCount === uploadQueueItems.length

  return { onFilesSelected, onIntegrationFilesSelected, uploadQueueItems, readyCount, allItemsReady, onClear, onRetry }
}

export const useSafeTimeout = () => {
  const ref = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSafeTimeout = () => {
    if (ref.current) {
      clearTimeout(ref.current)
    }
  }

  useEffect(() => {
    return () => {
      clearSafeTimeout()
    }
  }, [])

  const setSafeTimeout = (callback: () => void, delay?: number) => {
    ref.current = setTimeout(callback, delay)
  }

  return { setSafeTimeout, clearSafeTimeout }
}

export const useProcessDocumentFileInput = (supportedMimeTypes: string[], onFilesSelected: (files: File[]) => Promise<void>, disabled = false) => {
  const [isHovering, setIsHovering] = useState<boolean>(false)

  useEffect(() => {
    const handleDrop = (e: DragEvent) => {
      e.preventDefault()
    }

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault()
    }

    window.addEventListener('drop', handleDrop)
    window.addEventListener('dragover', handleDragOver)

    return () => {
      window.removeEventListener('drop', handleDrop)
      window.removeEventListener('dragover', handleDragOver)
    }
  }, [])

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    if (disabled) {
      return
    }

    const input = e.target
    const { files } = input
    if (!files?.length) {
      return
    }

    const filteredFiles = Array.from(files).filter((file) => supportedMimeTypes.includes(file.type))

    input.value = ''

    if (!filteredFiles.length) {
      return
    }

    await onFilesSelected(filteredFiles)
  }

  const handleDrop = async (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setIsHovering(false)

    if (disabled) {
      return
    }

    const { dataTransfer } = e
    const { files } = dataTransfer
    if (!files.length) {
      return
    }

    const filteredFiles = Array.from(files).filter((file) => supportedMimeTypes.includes(file.type))
    if (!filteredFiles.length) {
      return
    }

    await onFilesSelected(filteredFiles)
  }

  const handleDragEnter = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled) {
      return
    }

    setIsHovering(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsHovering(false)
  }

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return {
    isHovering,
    handleChange,
    handleDrop,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
  }
}

const connectorItemToDocumentFileInfo = (item: DocumentConnectorItem): DocumentFileInfo => ({
  fileId: item.integration?.externalId ?? item.id ?? '',
  fileName: item.name,
  fileSize: item.size ?? 0,
  contentType: item.contentType ?? '',
})

export const useDocumentFile = (documentFilesToInitialize?: DocumentFile[]) => {
  const accountId = useAppSelector(selectAccountId)

  const [triggerGetFileById] = useLazyGetFileByIdQuery()
  const [triggerGetDocumentFileById] = useLazyGetDocumentConnectorFileQuery()

  const documentFile = documentFilesToInitialize?.[0]

  const { data: me, isFetching: meFetching } = useGetMeQuery({ accountId })

  const connectedDocumentServiceIds = useMemo(() => getConnectedDocumentServiceIds(me), [me])

  const { data: zocksFileInfo, isFetching: zocksFetching } = useGetFileByIdQuery(
    documentFile?.type === DocumentFileType.ZOCKS ? { accountId, fileId: documentFile.fileId } : skipToken,
  )
  const { data: integrationFileInfo, isFetching: integrationFetching } = useGetDocumentConnectorFileQuery(
    documentFile?.type === DocumentFileType.INTEGRATION && connectedDocumentServiceIds.includes(documentFile.connector as IntegrationServiceId)
      ? { accountId, connector: documentFile.connector, fileId: documentFile.fileId }
      : skipToken,
  )

  const fetchZocksDocumentFile = useCallback(
    async (fileId: string): Promise<FileInfo | null> => {
      try {
        return await triggerGetFileById({ accountId, fileId }).unwrap()
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerGetFileById],
  )

  const fetchIntegrationDocumentFile = useCallback(
    async (connector: string, fileId: string): Promise<DocumentFileInfo | null> => {
      try {
        const item = await triggerGetDocumentFileById({ accountId, connector, fileId }).unwrap()
        return item ? connectorItemToDocumentFileInfo(item) : null
      } catch (e) {
        console.error(e)
        return null
      }
    },
    [accountId, triggerGetDocumentFileById],
  )

  const fetchDocumentFile = useCallback(
    async (documentFile: DocumentFile): Promise<FileInfo | DocumentFileInfo | null> => {
      if (documentFile.type === DocumentFileType.INTEGRATION) {
        if (!connectedDocumentServiceIds.includes(documentFile.connector as IntegrationServiceId)) {
          return null
        }
        return await fetchIntegrationDocumentFile(documentFile.connector, documentFile.fileId)
      }
      return await fetchZocksDocumentFile(documentFile.fileId)
    },
    [connectedDocumentServiceIds, fetchIntegrationDocumentFile, fetchZocksDocumentFile],
  )

  const fileInfo =
    documentFile?.type === DocumentFileType.INTEGRATION
      ? integrationFileInfo
        ? connectorItemToDocumentFileInfo(integrationFileInfo)
        : integrationFileInfo
      : zocksFileInfo

  return { fetchDocumentFile, isLoading: integrationFetching || zocksFetching || meFetching, fileInfo }
}

export const useDocumentSummaryAiAgentResult = () => {
  const accountId = useAppSelector(selectAccountId)
  const document = useAppSelector(selectDocument)

  const { currentData: aiAgentResults, refetch } = useListAiAgentResultQuery(
    document ? { accountId, type: AIAgentType.DOCUMENT_SUMMARY, resultKey: document.id } : skipToken,
  )

  const refetchAiAgentResult = useCallback(async () => {
    try {
      const aiAgentResults = await refetch().unwrap()
      if (!aiAgentResults.length) {
        return null
      }
      return aiAgentResults[0]
    } catch (e) {
      console.error(e)
      return null
    }
  }, [refetch])

  return { aiAgentResult: aiAgentResults?.length ? aiAgentResults[0] : null, refetchAiAgentResult }
}

export const useAccountHasDocumentZdrPolicy = () => {
  const accountId = useAppSelector(selectAccountId)

  const { data: retentionPoliciesResponse } = useGetRetentionPoliciesQuery({ accountId })

  return useMemo(
    () =>
      !!retentionPoliciesResponse?.data.find(({ resource, rule }) => resource.type === RetentionResourceType.DOCUMENT && rule.type === RetentionRuleType.ZERO),
    [retentionPoliciesResponse],
  )
}
